import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStores } from '../../../stores';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Tag,
  Space,
  Empty,
  Spin,
  Tooltip
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  CalendarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './Appointments.scss';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const STATUS_COLORS = {
  PENDING: 'orange',
  CONFIRMED: 'blue',
  COMPLETED: 'green',
  CANCELLED: 'red'
};

const PartnerAppointmentsList = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerAppointmentStore, partnerServiceStore } = useStores();
  const { filteredAppointments, loading, statusFilter, serviceFilter, searchQuery } = partnerAppointmentStore;

  useEffect(() => {
    partnerAppointmentStore.fetchAppointments();
    partnerServiceStore.fetchServices();
  }, [partnerAppointmentStore, partnerServiceStore]);

  const handleSearch = (value) => {
    partnerAppointmentStore.setSearchQuery(value);
  };

  const handleStatusFilter = (value) => {
    partnerAppointmentStore.setStatusFilter(value);
  };

  const handleServiceFilter = (value) => {
    partnerAppointmentStore.setServiceFilter(value);
  };

  const handleDateRangeFilter = (dates) => {
    if (dates && dates.length === 2) {
      partnerAppointmentStore.setDateRangeFilter({
        start: dates[0].format('YYYY-MM-DD'),
        end: dates[1].format('YYYY-MM-DD')
      });
    } else {
      partnerAppointmentStore.setDateRangeFilter(null);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      PENDING: t('partner.appointments.status.pending'),
      CONFIRMED: t('partner.appointments.status.confirmed'),
      COMPLETED: t('partner.appointments.status.completed'),
      CANCELLED: t('partner.appointments.status.cancelled')
    };
    return statusMap[status] || status;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      fixed: 'left',
      render: (id) => <strong>#{id}</strong>
    },
    {
      title: t('partner.appointments.columnHeaders.service'),
      dataIndex: 'service_name',
      key: 'service_name',
      width: 150,
      ellipsis: true,
      render: (name) => (
        <Tooltip title={name}>
          <Tag color="blue" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </Tag>
        </Tooltip>
      )
    },
    {
      title: t('partner.appointments.columnHeaders.customer'),
      key: 'customer',
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        const fullName = `${record.customer_first_name} ${record.customer_last_name}`;
        return (
          <Tooltip title={`${fullName} - ${record.customer_email}`}>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fullName}
              </div>
              <div style={{ fontSize: '12px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {record.customer_email}
              </div>
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: t('partner.appointments.columnHeaders.animal'),
      dataIndex: 'pet_name',
      key: 'pet_name',
      width: 100,
      ellipsis: true,
      render: (name) => name ? (
        <Tooltip title={name}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {name}
          </span>
        </Tooltip>
      ) : <span style={{ color: '#ccc' }}>-</span>
    },
    {
      title: t('partner.appointments.columnHeaders.dateTime'),
      key: 'datetime',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div>
            <CalendarOutlined /> {dayjs(record.appointment_date).format('DD/MM/YYYY')}
          </div>
          <div>
            <ClockCircleOutlined /> {record.start_time} ({record.duration} {t('common.minutes')})
          </div>
        </Space>
      ),
      sorter: (a, b) => {
        const dateA = dayjs(`${a.appointment_date} ${a.start_time}`);
        const dateB = dayjs(`${b.appointment_date} ${b.start_time}`);
        return dateA.unix() - dateB.unix();
      },
      defaultSortOrder: 'ascend'
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={STATUS_COLORS[status]}>{getStatusText(status)}</Tag>
      ),
      filters: [
        { text: t('partner.appointments.status.pending'), value: 'PENDING' },
        { text: t('partner.appointments.status.confirmed'), value: 'CONFIRMED' },
        { text: t('partner.appointments.status.completed'), value: 'COMPLETED' },
        { text: t('partner.appointments.status.cancelled'), value: 'CANCELLED' }
      ],
      onFilter: (value, record) => record.status === value
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/partner/appointments/${record.id}`)}
        >
          {t('common.view')}
        </Button>
      )
    }
  ];

  return (
    <div className="partner-appointments-list">
      <div className="page-header">
        <h1>{t('partner.appointments.title')}</h1>
      </div>

      <Card className="filters-card">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Search
              placeholder={t('partner.appointments.searchPlaceholder')}
              allowClear
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />

            <Select
              placeholder={t('partner.appointments.filterByStatus')}
              allowClear
              style={{ width: 150 }}
              value={statusFilter}
              onChange={handleStatusFilter}
            >
              <Option value="PENDING">{t('partner.appointments.status.pending')}</Option>
              <Option value="CONFIRMED">{t('partner.appointments.status.confirmed')}</Option>
              <Option value="COMPLETED">{t('partner.appointments.status.completed')}</Option>
              <Option value="CANCELLED">{t('partner.appointments.status.cancelled')}</Option>
            </Select>

            <Select
              placeholder={t('partner.appointments.filterByService')}
              allowClear
              style={{ width: 200 }}
              value={serviceFilter}
              onChange={handleServiceFilter}
            >
              {partnerServiceStore.services.map(service => (
                <Option key={service.id} value={service.id}>
                  {service.name_en}
                </Option>
              ))}
            </Select>

            <RangePicker
              onChange={handleDateRangeFilter}
              format="DD/MM/YYYY"
            />
          </Space>
        </Space>
      </Card>

      <Card>
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
          </div>
        ) : filteredAppointments.length === 0 ? (
          <Empty
            description={t('partner.appointments.noAppointments')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredAppointments}
            rowKey="id"
            scroll={{ x: 850 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${total} ${t('partner.appointments.title')}`
            }}
          />
        )}
      </Card>
    </div>
  );
});

export default PartnerAppointmentsList;
